// Воронка кандидатов. Главное представление раздела: здесь рекрутёр проводит рабочий день.
//
// Одна строка — одна грамматика (правило 2): кто, где он в воронке и что с ним не так. Над
// списком сказано, сколько кандидатов в ЭТОЙ выборке (total из ответа, а не длина показанного
// куска) и на какой момент срез (generated_at, а не часы браузера).
//
// Отбор целиком лежит в адресе (правило 4) и целиком уезжает на сервер: фильтр, который
// украшает экран, но не меняет выборку, — это враньё в счётчике над списком.
//
// Правило 11: при смене отбора строки НЕ исчезают — keepPreviousData держит прежний список,
// скелет рисуется только когда данных нет даже в кэше прошлой сессии.

import { RiAddLine, RiUserSearchLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { ListPane } from '@/layout/ListPane';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatPhone } from '@/shared/text/phone';
import { parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { SearchFilter } from '@/shared/ui/search-filter';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';

import type { CandidatesQuery } from '../api/candidates';
import { CANDIDATE_STATUSES } from '../api/candidates';
import { OwnerFilter } from '../components/OwnerFilter';
import { useCan } from '../components/gates';
import {
  CANDIDATE_FORMS,
  CANDIDATE_STATUS_COLORS,
  CANDIDATE_STATUS_TITLES,
  CHECK_RESULT_TITLES,
} from '../dictionaries';
import { useCandidatesQuery } from '../queries';
import type { HiringRoute } from '../route';
import {
  BLANK,
  FILTER_CITY,
  FILTER_OWNER,
  FILTER_STATUS,
  FILTER_TAG,
  PANEL_NEW_CANDIDATE,
  hiringHref,
  openPanelHref,
  setFilter,
} from '../route';

/** Заведение и правка карточек кандидата: server/app/hiring/api/candidates.py. */
const EDIT_PERMISSION = 'hiring.candidate.edit';

/**
 * Отбор из адреса, переведённый в параметры запроса. Единственное место, где адрес
 * превращается в запрос: два таких места разъезжаются, и человек видит в списке не то,
 * что написано в его же ссылке.
 */
export function queryFromRoute(search: SearchState): CandidatesQuery {
  return {
    status: search.filters[FILTER_STATUS],
    owner_user_id: search.filters[FILTER_OWNER],
    city: search.filters[FILTER_CITY],
    tag: search.filters[FILTER_TAG],
    q: search.q,
  };
}

export function CandidatesList({
  route,
}: {
  route: HiringRoute;
}) {
  const navigate = useNavigate();
  const { view, search, rowId } = route;
  const canEdit = useCan(EDIT_PERMISSION);

  const params = queryFromRoute(search);
  const query = useCandidatesQuery(params);

  function apply(next: SearchState) {
    // replace: в истории браузера один шаг на подбор отбора, а не двадцать.
    navigate(hiringHref(view, next, rowId), { replace: true });
  }

  const наложенОтбор =
    search.q !== undefined ||
    search.filters[FILTER_STATUS] !== undefined ||
    search.filters[FILTER_OWNER] !== undefined ||
    search.filters[FILTER_CITY] !== undefined ||
    search.filters[FILTER_TAG] !== undefined;

  const метка = search.filters[FILTER_TAG];

  const поиск = (
    <SearchFilter
      label='Поиск кандидата по фамилии, имени или телефону'
      placeholder='Фамилия, имя, телефон'
      value={search.q ?? ''}
      onApply={(value) => apply({ ...search, q: value === '' ? undefined : value })}
    />
  );

  const наложено = [
    search.filters[FILTER_STATUS] === undefined ? null : 'этап',
    search.filters[FILTER_OWNER] === undefined ? null : 'ответственный',
    метка === undefined ? null : `метка «${метка}»`,
  ].filter((имя): имя is string => имя !== null);

  const filters = (
    <>
      <SelectField
        id='hiring-stage'
        label='Этап воронки'
        value={search.filters[FILTER_STATUS] ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_STATUS, value === NO_VALUE ? undefined : value))
        }
        options={CANDIDATE_STATUSES.map((code) => ({
          value: code,
          label: CANDIDATE_STATUS_TITLES[code],
        }))}
        noneLabel='Любой этап'
        placeholder='Любой этап'
      />

      <OwnerFilter
        value={search.filters[FILTER_OWNER]}
        onChange={(value) => apply(setFilter(search, FILTER_OWNER, value))}
      />

      {/* Метка не имеет своего поля ввода: она ставится в карточке и приходит сюда ссылкой
          или переходом. Но раз отбор наложен, снять его обязано быть можно прямо здесь —
          иначе человек видит короткий список и не понимает, почему он короткий. */}
      {метка !== undefined && (
        <div className='flex items-center justify-between gap-2'>
          <span className='min-w-0 truncate text-paragraph-sm text-text-sub-600'>
            Только с меткой «{метка}»
          </span>
          <Button.Root
            type='button'
            variant='neutral'
            mode='ghost'
            size='xsmall'
            onClick={() => apply(setFilter(search, FILTER_TAG, undefined))}
          >
            Снять
          </Button.Root>
        </div>
      )}
    </>
  );

  return (
    <ListPane
      caption={
        query.data === undefined ? 'Кандидаты' : countLabel(query.data.total, CANDIDATE_FORMS)
      }
      sliceAt={parseMoment(query.data?.generated_at)}
      search={поиск}
      filters={filters}
      appliedFilters={наложено}
      width='имена'
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='воронку кандидатов'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiUserSearchLine}
            title={наложенОтбор ? 'Под этот отбор кандидатов нет' : 'В воронке пока никого нет'}
            illustration={наложенОтбор ? undefined : 'люди'}
            description={
              наложенОтбор
                ? 'Список пуст из-за наложенного отбора, а не потому, что кандидатов нет вовсе. Снимите поиск, этап или ответственного.'
                : 'Кандидаты появляются двумя путями: откликом с площадки «Авито» и заведением руками. Первый работает сам, как только у учётной записи площадки есть доступы.'
            }
            // Приглашение завести первого стоит в правой колонке, где есть и место, и
            // объяснение: два сообщения об одном человек читает как два разных и ищет
            // между ними разницу, которой нет.
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((candidate) => (
              <ListRow
                key={candidate.id}
                to={hiringHref(view, search, candidate.id)}
                selected={candidate.id === rowId}
                title={candidate.display_name}
                // Метка в строке ровно ОДНА, и это этап воронки: он и есть грамматика этого
                // списка. Совпадение с чёрным списком при этом обязано быть видно, поэтому
                // оно стоит ПЕРВЫМ словом подписи — как «есть долг» в реестре людей. Второй
                // меткой справа оно съедало бы город и телефон, а метки не сжимаются.
                subtitle={подпись(candidate)}
                badge={
                  <Badge.Root
                    variant='lighter'
                    color={CANDIDATE_STATUS_COLORS[candidate.status]}
                    size='medium'
                  >
                    {CANDIDATE_STATUS_TITLES[candidate.status]}
                  </Badge.Root>
                }
              />
            ))}

            {data.items.length < data.total && (
              <div className='px-4 py-2'>
                <Hint.Root>
                  Показаны первые {data.items.length} из {data.total}. Сузьте отбор — иначе человек,
                  которого вы ищете, может остаться за пределами страницы.
                </Hint.Root>
              </div>
            )}

            {canEdit && (
              <div className='flex justify-center p-3'>
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='stroke'
                  size='xsmall'
                  onClick={() => navigate(openPanelHref(route, PANEL_NEW_CANDIDATE, BLANK))}
                >
                  <Button.Icon as={RiAddLine} />
                  Новый кандидат
                </Button.Root>
              </div>
            )}
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}

/**
 * Подпись строки. Порядок слов не косметика: сначала то, что меняет решение (совпадение с
 * чёрным списком), потом то, по чему человека узнают (город и телефон). Обрезается при
 * нехватке места хвост, и терять там телефон дешевле, чем предупреждение.
 */
function подпись(candidate: {
  blacklist_result: string | null;
  city: string | null;
  phone_normalized: string | null;
}): string {
  const части: string[] = [];
  if (candidate.blacklist_result === 'exact' || candidate.blacklist_result === 'possible') {
    части.push(CHECK_RESULT_TITLES[candidate.blacklist_result].toLowerCase());
  }
  части.push(candidate.city ?? 'город не указан');
  части.push(formatPhone(candidate.phone_normalized) ?? 'телефона нет');
  return части.join(' · ');
}
